import { Box } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { L, Icon } from '../ui/LavDropdown'
import { categoryColor } from './messagingUtils'
import { getServicePrice } from '../../utils/appointmentUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Upcoming appointment card for the client profile — color-coded by the
 * service's appointment color / category, with pet + service, add-ons,
 * groomer, and price.
 *
 * appt: appointments row with service_types / employees / pets joins and the
 * add_ons JSON column ([{ service_id, name, price, duration_minutes }]).
 */
function UpcomingCard({ appt, tz, formatCurrency }) {
	const accent = categoryColor(appt.service_types)
	const start = dayjs(appt.start_ts).tz(tz)
	const end = appt.end_ts ? dayjs(appt.end_ts).tz(tz) : null
	const dateLabel = start.isSame(dayjs().tz(tz), 'day') ? 'Today' : start.format('ddd, MMM D')
	const timeLabel = end
		? `${start.format('h:mm')} – ${end.format('h:mm A')}`
		: start.format('h:mm A')
	const addOns = Array.isArray(appt.add_ons) ? appt.add_ons : []
	const priceCents = getServicePrice(appt) + addOns.reduce((s, a) => s + (a.price || 0), 0)
	const groomer = appt.employees
		? `${appt.employees.first_name} ${appt.employees.last_name}`.trim()
		: 'Any groomer'

	return (
		<Box sx={{
			position: 'relative', overflow: 'hidden', border: `1px solid ${L.border}`,
			borderRadius: '12px', bgcolor: '#FFFFFF', p: '11px 13px 11px 16px',
			'&::before': {
				content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
				width: '4px', bgcolor: accent,
			},
		}}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', mb: '11px' }}>
				<Box component="strong" sx={{ fontSize: '14px', fontWeight: 750, color: L.text, letterSpacing: '-0.01em' }}>
					{dateLabel}
				</Box>
				<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#6E6886' }}>
					{timeLabel}
				</Box>
			</Box>
			<Box sx={{ display: 'flex', gap: '9px' }}>
				<Box sx={{ width: 8, height: 8, flex: 'none', borderRadius: '50%', bgcolor: accent, mt: '5px' }} />
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '7px', fontSize: '13px' }}>
						<Box component="strong" sx={{ flex: 'none', fontWeight: 700, color: L.text }}>
							{appt.pets?.name || 'Pet'}
						</Box>
						<Box component="span" sx={{
							minWidth: 0, fontWeight: 500, color: L.text,
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{appt.service_types?.name}
						</Box>
					</Box>
					{/* One add-on per line (design `.up-pet-addons`) — a comma-joined
					    run wraps unpredictably in the 290px profile sidebar. */}
					{addOns.map((a, i) => (
						<Box key={a.service_id ?? i} sx={{
							fontSize: '11.5px', fontWeight: 500, color: L.muted, mt: '2px',
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							+ {a.name}
						</Box>
					))}
				</Box>
			</Box>
			<Box sx={{
				display: 'flex', alignItems: 'center', gap: '7px', mt: '10px', pt: '9px',
				borderTop: `1px solid ${L.surface}`, fontSize: '12px', fontWeight: 600, color: L.muted,
			}}>
				<Icon name="user" size={12} />
				<Box component="span" sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
					{groomer}
				</Box>
				<Box component="span" sx={{ flex: 'none', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
					{formatCurrency(priceCents)}
				</Box>
			</Box>
		</Box>
	)
}

export default UpcomingCard
